import React, { useEffect, useState, useRef } from "react";
import { useFieldArray, Controller, useFormContext } from "react-hook-form";
import {
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  IconButton,
} from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import styled from "@emotion/styled";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import axios from "axios";
import useStateMaster from "../../../../../hooks/useStateMaster";
import usePincode from "../../../../../hooks/usePincode";
import SearchableAutoComplete from "../../../../../components/SearchableAutoComplete";
import CustomTextField from "../../../../../components/CustomTextField";
import ReactHookFormSelect from "../../../../../components/ReactHookFormSelect";

const statePayload = {
  filters: [],
  pagination: {
    pageNumber: 1,
    pageSize: 100,
  },
  sortFields: [],
};
const currentPayload = {
  filters: [],
  pagination: {
    pageNumber: 1,
    pageSize: 100,
  },
  sortFields: [],
};

const Item = styled(Paper)(({ theme }) => ({
  // backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
  fontWeight: "bold",
}));

const InvoiceSettings = ({ fields: validationFields }) => {
  const { searchState, stateSearchResults } = useStateMaster();
  const theme = useTheme();
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();

  const isMultipleGst = watch("isMultipleGst");

  const { fields, append, remove } = useFieldArray({
    control,
    name: "invoiceSettings",
  });

  const addItem = () => {
    const item = {
      invoicePrefix: "",
      lastInvoiceNumber: "0",
      tenantInvoiceCounterId: null,
      gstNumber: "",
      doorNo: "",
      pinCode: null,
      area: "",
      city: "",
      state: "",
      country: "",
      postOffices: [],
    };

    append(item);
  };

  const removeItem = (index) => {
    remove(index);
  };

  const fetchState = async () => {
    await searchState(statePayload);
  };

  useEffect(() => {
    fetchState();
  }, []);

  const [pinResults, setPinResults] = useState([]);
  const [payload, setPayload] = useState(currentPayload);

  useEffect(() => {
    autoSearchPincode(payload);
  }, [payload]);

  const {
    autoSearchPincode,
    pincodeAutoSearchResults,
    isLoadingAutoSearch: loadingPincode,
  } = usePincode();

  const searchInputChange = (data) => {
    let filters = [];
    filters.push({
      fieldName: "pincodeName",
      fieldValue: data,
      op: "like",
      dataType: "string",
      condition: "and",
      isJoin: false,
    });
    setPayload({
      ...payload,
      filters: filters,
      pagination: {
        pageNumber: 1,
        pageSize: 100,
      },
    });
  };
  useEffect(() => {
    if (pincodeAutoSearchResults.data.length !== 0) {
      setPinResults([...pinResults, ...pincodeAutoSearchResults.data]);
    }
  }, [pincodeAutoSearchResults]);

  const onChangeEvent = (name, pinCode) => {
    console.log(name, pinCode, "onChangeEvent");
    const splitName = name.split(".");
    const rowIndex = splitName[1];
    setValue(name, pinCode);
    console.log("invoiceSettings." + rowIndex + ".state", "datadatadatadata");
    if (pinCode != undefined && pinCode?.label?.length === 6) {
      axios
        .get(`https://api.postalpincode.in/pincode/${pinCode?.label}`)
        .then((response) => {
          const data = response.data[0];
          if (data.Status === "Success") {
            setValue(`invoiceSettings.${rowIndex}.postOffices`, data.PostOffice);
            setValue("invoiceSettings." + rowIndex + ".state", data.PostOffice[0]?.State || "");
            setValue("invoiceSettings." + rowIndex + ".country", data.PostOffice[0]?.Country || "");
          } else {
            setValue(`invoiceSettings.${rowIndex}.postOffices`, []);
            setValue("invoiceSettings." + rowIndex + ".state", "");
            setValue("invoiceSettings." + rowIndex + ".country", "");
          }
        })
        .catch(() => {
          setValue(`invoiceSettings.${rowIndex}.postOffices`, []);
          setValue("invoiceSettings." + rowIndex + ".state", "");
          setValue("invoiceSettings." + rowIndex + ".country", "");
        });
    } else {
      setValue(`invoiceSettings.${rowIndex}.postOffices`, []);
      setValue("invoiceSettings." + rowIndex + ".state", "");
      setValue("invoiceSettings." + rowIndex + ".country", "");
    }
  };

  // useEffect(() => {
  //   //console.log(pinCode?.label);
  //   if (pinCode != undefined && pinCode?.label?.length === 6) {
  //     axios
  //       .get(`https://api.postalpincode.in/pincode/${pinCode?.label}`)
  //       .then((response) => {
  //         const data = response.data[0];
  //         if (data.Status === "Success") {
  //           setPostOffices(data.PostOffice);
  //           setValue("state", data.PostOffice[0]?.State || "");
  //           setValue("country", data.PostOffice[0]?.Country || "");
  //         } else {
  //           setPostOffices([]);
  //           setValue("state", "");
  //           setValue("country", "");
  //         }
  //       })
  //       .catch(() => {
  //         setPostOffices([]);
  //         setValue("state", "");
  //         setValue("country", "");
  //       });
  //   }
  // }, [pinCode]);

  return (
    <Grid container spacing={6} className="row" sx={{ mb: 20 }}>
      <Grid item md={12}>
        <Box sx={{ height: 400, overflowY: "auto" }}>
          {fields.map((field, index) => {
            const fieldErrors = errors.invoiceSettings?.[index];
            const postOffices = watch(`invoiceSettings.${index}.postOffices`) || [];
            return (
              <Grid container spacing={2} key={index} sx={{ mt: 3 }}>
                <Paper
                  elevation={2}
                  sx={{
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                    p: 2,
                    background: "#fafafa",
                  }}
                >
                  {/* Section Heading */}
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      fontWeight: "bold",
                      borderBottom: "2px solid #1976d2",
                      pb: 1,
                    }}
                  >
                    Invoice Setting #{index + 1}
                  </Typography>
                  {/* Wrap inputs inside Grid container */}
                  <Grid container spacing={2} sx={{ mt: 5 }}>
                    {/* Invoice Prefix */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Item theme={theme}>
                        <Controller
                          name={`invoiceSettings.${index}.invoicePrefix`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              fullWidth
                              label={t("invoicePrefix")}
                              {...field}
                              error={!!fieldErrors?.invoicePrefix}
                              helperText={fieldErrors?.invoicePrefix?.message}
                            />
                          )}
                        />
                      </Item>
                    </Grid>

                    {/* Last Invoice No */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Item theme={theme}>
                        <Controller
                          name={`invoiceSettings.${index}.lastInvoiceNumber`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              fullWidth
                              label={t("lastInvoiceNumber")}
                              {...field}
                              error={!!fieldErrors?.lastInvoiceNumber}
                              helperText={fieldErrors?.lastInvoiceNumber?.message}
                            />
                          )}
                        />
                      </Item>
                    </Grid>

                    {/* GST Number */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Item theme={theme}>
                        <Controller
                          name={`invoiceSettings.${index}.gstNumber`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              fullWidth
                              label={t("gstNumber")}
                              {...field}
                              error={!!fieldErrors?.gstNumber}
                              helperText={fieldErrors?.gstNumber?.message}
                            />
                          )}
                        />
                      </Item>
                    </Grid>

                    {/* Door No */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Item theme={theme}>
                        <Controller
                          name={`invoiceSettings.${index}.doorNo`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              fullWidth
                              label={t("door_no")}
                              {...field}
                              error={!!fieldErrors?.doorNo}
                              helperText={fieldErrors?.doorNo?.message}
                            />
                          )}
                        />
                      </Item>
                    </Grid>

                    {/* Pincode */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Item theme={theme}>
                        <SearchableAutoComplete
                          control={control}
                          data={pinResults}
                          label={t("pincode_name")}
                          name={`invoiceSettings.${index}.pinCode`}
                          payload={payload}
                          setPayload={setPayload}
                          loading={loadingPincode}
                          searchInputChange={searchInputChange}
                          searchLength="3"
                          onChangeEvent={onChangeEvent}
                        />
                      </Item>
                    </Grid>

                    {/* Area */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Item theme={theme}>
                        <ReactHookFormSelect
                          name={`invoiceSettings.${index}.area`}
                          label={t("select_area")}
                          control={control}
                          fullWidth
                          errors={errors}
                          fields={validationFields}
                        >
                          {postOffices?.map((po) => (
                            <MenuItem key={po.Name} value={po.Name}>
                              {po.Name}
                            </MenuItem>
                          ))}
                        </ReactHookFormSelect>
                      </Item>
                    </Grid>

                    {/* City */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Item theme={theme}>
                        <CustomTextField
                          label={t("city_town")}
                          name={`invoiceSettings.${index}.city`}
                          control={control}
                          fullWidth
                          errors={errors}
                        />
                      </Item>
                    </Grid>

                    {/* State Display */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Item theme={theme}>
                        <CustomTextField
                          label={t("state")}
                          name={`invoiceSettings.${index}.state`}
                          control={control}
                          fullWidth
                          disabled
                          errors={errors}
                        />
                      </Item>
                    </Grid>

                    {/* Country */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Item theme={theme}>
                        <CustomTextField
                          label={t("country")}
                          name={`invoiceSettings.${index}.country`}
                          control={control}
                          fullWidth
                          disabled
                          errors={errors}
                        />
                      </Item>
                    </Grid>

                    {/* Remove & Add Buttons */}
                    <Grid item xs={12} sm={6} md={1}>
                      {index !== 0 && (
                        <IconButton color="error" onClick={() => removeItem(index)}>
                          <Remove />
                        </IconButton>
                      )}
                    </Grid>

                    <Grid item xs={12} sm={6} md={1}>
                      {index === fields.length - 1 && (
                        <IconButton color="primary" onClick={addItem} disabled={!isMultipleGst}>
                          <Add />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            );
          })}
        </Box>
      </Grid>
    </Grid>
  );
};

export default InvoiceSettings;
